/**
 * Pet Guard scan alert — top of the dashboard, mirrors `SafeIdAlertBanner`.
 *
 * Surfaces unread pet-tag scans so the owner notices the moment they open
 * any dashboard page (the existing per-page `PetScanAlerts` on `/pets`
 * was easy to miss).
 *
 * Real-time updates: subscribes to `pet.qr_scanned` over the family
 * WebSocket and fires a toast + browser Notification when one arrives.
 * Dismiss uses the existing `markScanRead` endpoint (server-side ack),
 * which is better than the time-window hack we use for Safe ID.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, PawPrint, AlertOctagon, X } from 'lucide-react';

import { petsApi, type Pet, type PetScan } from '@/api/pets';
import { useFamilyEvents, type KidzioEvent } from '@/realtime/useFamilyEvents';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

function relativeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

function mapLink(lat: number, lng: number, pet: Pick<Pet, 'name' | 'photo_url'>): string {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    pin_label: pet.name.slice(0, 2).toUpperCase(),
    pin_title: pet.name, // shown on hover instead of the 2-letter label
    pin_color: '#f59e0b', // amber — matches Pet Guard
  });
  // When the pet has a photo, the map pin renders the photo (cropped
  // round) instead of the label-on-amber-circle fallback. Photo URL is
  // stored API-relative; the marker <img> needs an absolute URL.
  if (pet.photo_url) {
    const abs = pet.photo_url.startsWith('/') ? `${API_BASE}${pet.photo_url}` : pet.photo_url;
    params.set('pin_photo', abs);
  }
  return `/map?${params.toString()}`;
}

export function PetGuardAlertBanner() {
  const qc = useQueryClient();

  const scans = useQuery({
    queryKey: ['pets', 'scans'],
    queryFn: () => petsApi.scans(),
    retry: false,
    refetchInterval: 60_000,
  });
  const pets = useQuery({
    queryKey: ['pets', 'list'],
    queryFn: () => petsApi.list(),
    retry: false,
    staleTime: 60_000,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => petsApi.markScanRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pets', 'scans'] }),
  });

  // Ask for Notification permission only after the first scan lands, not
  // pre-emptively on a quiet dashboard.
  const askedPermissionRef = useRef(false);
  useEffect(() => {
    if (
      !askedPermissionRef.current &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      (scans.data?.filter((s) => !s.read_at).length ?? 0) > 0
    ) {
      askedPermissionRef.current = true;
      void Notification.requestPermission();
    }
  }, [scans.data]);

  useFamilyEvents((evt: KidzioEvent) => {
    if (evt.type !== 'pet.qr_scanned') return;
    const petName = pets.data?.find((p) => p.id === evt.data.pet_id)?.name ?? 'A pet';
    const body = evt.data.with_location
      ? `${petName}'s tag was scanned. Finder shared their location.`
      : `${petName}'s tag was just scanned.`;
    toast.warning('Pet tag scanned', { description: body, duration: 12_000 });
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        new Notification('Kidzio · Pet tag scanned', {
          body,
          icon: '/logo-mark.svg',
          tag: `pet-${evt.data.pet_id}`,
          requireInteraction: !!evt.data.with_location,
        });
      } catch {
        /* notification spec: failures are non-fatal */
      }
    }
    void qc.invalidateQueries({ queryKey: ['pets', 'scans'] });
  });

  const unread = (scans.data ?? []).filter((s) => !s.read_at);
  if (unread.length === 0) return null;

  // Keep the full pet (name + photo_url) so the map deep-link can include
  // the photo. Falls back to a name-only synthetic pet when the roster
  // hasn't loaded or the pet was removed since the scan was logged.
  const petById = new Map((pets.data ?? []).map((p) => [p.id, p]));
  const rows = unread.slice(0, 3);

  return (
    <div className="mx-auto mb-4 max-w-7xl rounded-3xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm sm:px-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-800 shadow-sm">
          <AlertOctagon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-amber-900">
            {unread.length === 1
              ? "Someone scanned a pet's tag"
              : `${unread.length} pet-tag scans unread`}
          </p>
          <p className="text-xs text-amber-800/80">
            A neighbour may have found your pet — review and reply if needed.
          </p>
          <ul className="mt-2 grid gap-1.5">
            {rows.map((s: PetScan) => {
              const pet = petById.get(s.pet_id);
              const petInfo = pet ?? { name: 'Pet', photo_url: null };
              const hasCoords = s.approx_lat != null && s.approx_lng != null;
              return (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center gap-2 text-xs text-amber-900"
                >
                  <PawPrint className="size-3.5 shrink-0 opacity-70" />
                  <span className="font-semibold">{petInfo.name}</span>
                  <span className="opacity-70">·</span>
                  <span>{relativeShort(s.scanned_at)}</span>
                  {s.message && (
                    <>
                      <span className="opacity-70">·</span>
                      <span className="italic">&ldquo;{s.message}&rdquo;</span>
                    </>
                  )}
                  {s.finder_contact && (
                    <>
                      <span className="opacity-70">·</span>
                      {/* Best-effort tap-to-call. If the finder typed a
                          name (not a phone), the link still works — the
                          phone app just refuses to dial and shows it as
                          text. No regex-validation: false negatives are
                          worse than a broken-looking dialler prompt. */}
                      <a
                        href={`tel:${s.finder_contact.replace(/[^+\d]/g, '')}`}
                        className="font-mono font-semibold text-amber-800 underline-offset-2 hover:underline"
                      >
                        {s.finder_contact}
                      </a>
                    </>
                  )}
                  {hasCoords && (
                    <>
                      <span className="opacity-70">·</span>
                      <Link
                        to={mapLink(s.approx_lat!, s.approx_lng!, petInfo)}
                        className="inline-flex items-center gap-1 font-semibold text-amber-800 underline-offset-2 hover:underline"
                      >
                        <MapPin className="size-3" />
                        View on map
                      </Link>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => dismiss.mutate(s.id)}
                    disabled={dismiss.isPending}
                    aria-label="Mark this scan read"
                    className="ml-auto inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                  >
                    <X className="size-3" />
                    Read
                  </button>
                </li>
              );
            })}
          </ul>
          {unread.length > rows.length && (
            <p className="mt-1.5 text-[11px] text-amber-800/70">
              +{unread.length - rows.length} more.
            </p>
          )}
        </div>
        <Link
          to="/pets"
          className="shrink-0 rounded-full bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-amber-700"
        >
          Open Pet Guard
        </Link>
      </div>
    </div>
  );
}
